import { LeaderboardTrialInvitesPopupComponent } from '../leaderboard-trial-invites-popup/leaderboard-trial-invites-popup.component';
import { PopupService } from '@app/shared/popup';
import { TrialBannerService } from '@app/shell/trial-banner/service/trial-banner.service';
import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Input,
    createEnvironmentInjector,
    ApplicationRef,
    inject,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { LeaderboardPreviewGamificationService } from '../../service/leaderboard-preview-gamification/leaderboard-preview-gamification.service';
import { TranslateService } from '@ngx-translate/core';
import { UserSimpleGamification } from '@app/leaderboard/model/user.model';
import { map, tap } from 'rxjs/operators';

@Component({
    selector: 'app-leaderboard-preview-gamification',
    templateUrl: './leaderboard-preview-gamification.component.html',
    styleUrls: ['./leaderboard-preview-gamification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LeaderboardPreviewGamificationComponent implements OnInit, OnDestroy {
    private readonly appRef = inject(ApplicationRef);
    private readonly leaderboardPreviewGamificationService = inject(LeaderboardPreviewGamificationService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly trialBannerService = inject(TrialBannerService);
    private readonly popupService = inject(PopupService);

    readonly form = this.leaderboardPreviewGamificationService.form;

    /**
     * Count of users in leadearboard preview
     */
    @Input() count!: number;

    @Input() name!: string;

    invitesTrial$ = this.trialBannerService.invitesTrial$;
    invitesLoading$ = this.invitesTrial$.pipe(map((data) => data.loading));

    private readonly subscriptions$ = new Subscription();

    get leaderboard() {
        return this.leaderboardPreviewGamificationService.leaderboard(5);
    }

    get loading() {
        return this.leaderboardPreviewGamificationService.loading;
    }

    get dropdownItems() {
        return this.leaderboardPreviewGamificationService.ranges.map((name) => {
            return {
                id: name,
                name: this.rangeLabel(name),
            };
        });
    }

    get hasTrial() {
        return this.trialBannerService.hasTrial;
    }

    get sendedAllInvites() {
        const invites = this.trialBannerService.invitesTrial;
        return invites.max === invites.sent;
    }

    get showTrialInvite() {
        return this.hasTrial && !this.sendedAllInvites;
    }

    get showTrialInvitesInfo() {
        return this.hasTrial && this.leaderboard && this.leaderboard.length === 1;
    }

    ngOnInit() {
        this.subscriptions$.add(this.leaderboardPreviewGamificationService.updates.pipe(tap(() => this.changeDetectorRef.detectChanges())).subscribe());

        if (this.hasTrial) {
            this.subscriptions$.add(this.invitesTrial$.pipe(tap(() => this.changeDetectorRef.detectChanges())).subscribe());
            this.subscriptions$.add(
                this.trialBannerService.updateLeaderboard$.pipe(tap(() => this.leaderboardPreviewGamificationService.trialResetLeaderboard())).subscribe(),
            );
            if (this.trialBannerService.invitesTrial.loaded) {
                this.trialBannerService.getTrial(true);
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions$.unsubscribe();
    }

    itemTrendColor(item: UserSimpleGamification): string {
        switch (item.trend) {
            case 'success':
                return 'secondary';
            case 'danger':
                return 'danger-light';
            case 'muted':
                return 'quaternary';
            default:
                return 'quaternary';
        }
    }

    inviteTrialUsers() {
        if (!this.trialBannerService.invitesTrial.loading) {
            const environmentInjector = createEnvironmentInjector([{ provide: TrialBannerService, useValue: this.trialBannerService }], this.appRef.injector);

            this.popupService.open(LeaderboardTrialInvitesPopupComponent, null, null, { injector: environmentInjector });
        }
    }

    /**
     * returns user's fullname (name + surname) or "me" if he/she is currently logged in
     * @param user user data
     */
    getUserFullName(user: UserSimpleGamification) {
        if (user.me) {
            return this.translateService.instant('v3-globale-me');
        }
        return `${user.firstname} ${user.surname}`;
    }

    private rangeLabel(key: string) {
        switch (key) {
            case 'week':
                return this.translateService.instant('v3-preview-leaderboard-range-week');
            case 'month':
                return this.translateService.instant('v3-preview-leaderboard-range-month');
            case 'all-time':
                return this.translateService.instant('v3-preview-leaderboard-range-all-time');
            case 'alltime':
                return this.translateService.instant('v3-preview-leaderboard-range-alltime');
            case 'yeartodate':
                return this.translateService.instant('v3-preview-leaderboard-range-year-to-date');
        }
    }
}
